import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import posthog from 'posthog-js'
import { Reorder, useDragControls } from 'framer-motion'
import { CiSearch } from 'react-icons/ci'
import { IoClose } from 'react-icons/io5'
import { MdDragIndicator } from 'react-icons/md'
import {
    applySavedOrder,
    matchesName,
    readSavedOrder,
    saveOrder,
} from 'utilities/helper/fundOrder'
import { fundTitle } from 'utilities/helper/format'
import Item from './item'

interface IItemsProps {
    moneyPools: Array<MoneyPool>
}

/**
 * Its own key, not home's. Home stores the funds you *manage*; this list is
 * every fund you are in. Sharing a key would let a save from either screen drop
 * the funds the other one knows about — see `utilities/helper/fundOrder`.
 */
const STORAGE_KEY = 'funds:moneypools-order'

/** Below this many funds a search box is clutter rather than help. */
const SEARCH_FROM = 5

/** How long typing has to stop before the search result is reported. */
const SEARCH_SETTLE_MS = 400

/**
 * The moneypool cards on the صندوق‌ها tab, reorderable and searchable.
 *
 * hamyan-pwa#31 asked for the home page's sorting here too: a member of a dozen
 * funds got them in whatever order the API sent, and these are full cards, so
 * "the one I pay into every week" could be four screens down with no way to
 * move it up and no way to search for it.
 *
 * Same mechanics as `pages/dashboard/home/ManagedFunds`, and deliberately so —
 * the merge rules and the Persian-folding search are the one shared module, the
 * handle sits at the right edge (first DOM child of an RTL row), and the order
 * is per-browser localStorage rather than server state. The differences are
 * only what the two screens are:
 *
 * - Nothing is collapsed here, so `onReorder` sees the whole list and there is
 *   no prefix to preserve. Dragging is still off while searching, where the
 *   rendered rows are a filtered subset.
 * - The baseline order is the API's own. Home ranks owned funds first because
 *   it only lists funds you run; this tab lists funds you are merely a member
 *   of too, and nothing here beats what the server already decided.
 *
 * Still a fragment, not a wrapper `div`: the cards are direct children of the
 * tab's `flex flex-col gap-3` column so every card on the screen — hero,
 * cashbox, banner, moneypool — sits on the same rhythm. The `Reorder.Group`
 * carries that same `gap-3` for the rows it now owns.
 */
export default function MoneyPools({ moneyPools }: IItemsProps) {
    const [query, setQuery] = useState('')
    const [savedIds, setSavedIds] = useState<number[]>(() =>
        readSavedOrder(STORAGE_KEY)
    )

    // The live order and where the current drag picked its card up from.
    // `onReorder` runs on every intermediate swap, so neither can be read off a
    // closure captured when the gesture started — see `onDragEnd`.
    const orderRef = useRef<number[]>([])
    const dragFrom = useRef<number | null>(null)

    const ordered = useMemo(
        () => applySavedOrder(moneyPools, savedIds),
        [moneyPools, savedIds]
    )

    const searching = query.trim().length > 0
    const shown = useMemo(
        () =>
            searching
                ? ordered.filter((mp) => matchesName(mp.name, query))
                : ordered,
        [ordered, query, searching]
    )

    orderRef.current = ordered.map((mp) => mp.id)

    // «found nothing» is the case that decides whether the box is worth
    // keeping, and no other event can see it. Debounced so that «رض» on the way
    // to «رضایی» is not counted as a search that failed.
    useEffect(() => {
        if (!searching) return
        const timer = window.setTimeout(() => {
            posthog.capture('funds_list_search_result', {
                funds_count: ordered.length,
                result_count: shown.length,
                is_empty: shown.length === 0,
            })
        }, SEARCH_SETTLE_MS)
        return () => window.clearTimeout(timer)
        // Keyed on the query and the size of its answer; `shown` itself is a
        // new array on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, searching, shown.length, ordered.length])

    const sortable = !searching && ordered.length > 1

    const persist = (next: MoneyPool[]) => {
        const ids = next.map((mp) => mp.id)
        saveOrder(STORAGE_KEY, ids)
        setSavedIds(ids)
    }

    const onReorder = (ids: number[]) => {
        const byId = new Map(ordered.map((mp) => [mp.id, mp]))
        const next = ids
            .map((id) => byId.get(id))
            .filter((mp): mp is MoneyPool => !!mp)
        persist(next)
    }

    /**
     * One drag is one event.
     *
     * `onReorder` is not the gesture — framer-motion fires it once per
     * intermediate swap, so a card dragged from fourth place to first would
     * report three reorders and make a feature nobody had touched three times
     * look heavily used. The gesture's own start and end are the honest
     * boundary, and the distance between them is what the swaps were describing.
     */
    const onDragStart = (id: number) => {
        dragFrom.current = orderRef.current.indexOf(id)
    }

    const onDragEnd = (id: number) => {
        const from = dragFrom.current
        dragFrom.current = null
        const to = orderRef.current.indexOf(id)
        // Picked up and put back down: no order changed, nothing to report.
        if (from === null || from < 0 || to < 0 || to === from) return
        posthog.capture('funds_list_reordered', {
            method: 'drag',
            funds_count: ordered.length,
            moved_distance: Math.abs(to - from),
        })
    }

    /** Keyboard path for the same thing — framer-motion's drag has none. */
    const move = (id: number, delta: number) => {
        const from = ordered.findIndex((mp) => mp.id === id)
        const to = from + delta
        if (from < 0 || to < 0 || to >= ordered.length) return
        const next = [...ordered]
        next.splice(to, 0, next.splice(from, 1)[0])
        persist(next)
        posthog.capture('funds_list_reordered', {
            method: 'keyboard',
            funds_count: ordered.length,
            moved_distance: Math.abs(delta),
        })
    }

    // Once per search, on the empty → non-empty edge. One event per keystroke
    // would drown the funnel.
    const onQueryChange = (value: string) => {
        if (!searching && value.trim()) {
            posthog.capture('funds_list_search_used', {
                funds_count: ordered.length,
            })
        }
        setQuery(value)
    }

    return (
        <>
            {ordered.length >= SEARCH_FROM && (
                <div className="bg-white border border-divider rounded-card shadow-card px-3 py-2">
                    <div className="flex items-center gap-2 rounded-xl bg-canvas px-3 py-2">
                        <CiSearch
                            size={18}
                            className="shrink-0 text-text-muted"
                        />
                        <input
                            value={query}
                            onChange={(e) => onQueryChange(e.target.value)}
                            placeholder="جستجوی نام صندوق"
                            aria-label="جستجوی نام صندوق"
                            className="w-full bg-transparent text-sm outline-none placeholder:text-text-muted"
                        />
                        {searching && (
                            <button
                                type="button"
                                onClick={() => setQuery('')}
                                aria-label="پاک کردن جستجو"
                                className="shrink-0 cursor-pointer"
                            >
                                <IoClose className="text-text-muted" />
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Self-dismissing: the handle is a new affordance and needs to be
                named once, but a permanent caption over the list would be
                clutter. The first drag writes an order and the hint is gone for
                good. Above the cards rather than below them — under a column of
                full-height cards nobody would ever scroll to it. */}
            {sortable && savedIds.length === 0 && (
                <p className="px-1 text-[11px] text-text-muted text-right">
                    با کشیدن دستگیرهٔ گوشهٔ هر صندوق، ترتیب فهرست را تغییر دهید.
                </p>
            )}

            <Reorder.Group
                as="div"
                axis="y"
                values={shown.map((mp) => mp.id)}
                onReorder={onReorder}
                className="flex flex-col gap-3"
            >
                {shown.map((mp, index) => (
                    <SortableFund
                        key={mp.id}
                        item={mp}
                        sortable={sortable}
                        fromSearch={searching}
                        rowIndex={index}
                        onMove={(delta) => move(mp.id, delta)}
                        onDragStart={() => onDragStart(mp.id)}
                        onDragEnd={() => onDragEnd(mp.id)}
                    />
                ))}
            </Reorder.Group>

            {searching && shown.length === 0 && (
                <p className="bg-white border border-divider rounded-card shadow-card px-4 py-6 text-sm text-text-secondary text-center">
                    صندوقی با این نام پیدا نشد.
                </p>
            )}
        </>
    )
}

function SortableFund({
    item,
    sortable,
    fromSearch,
    rowIndex,
    onMove,
    onDragStart,
    onDragEnd,
}: {
    item: MoneyPool
    sortable: boolean
    fromSearch: boolean
    rowIndex: number
    onMove: (delta: number) => void
    onDragStart: () => void
    onDragEnd: () => void
}) {
    const controls = useDragControls()

    // The whole card is a navigation target, unlike the home rows where the
    // handle is a sibling of the button. Letting go of a drag fires a click
    // whose target is the card, so without this every reorder would also open
    // the fund. Cleared on the next task — the click is dispatched in the same
    // one as the pointerup that ended the drag.
    const justDragged = useRef(false)

    const handle: ReactNode = sortable ? (
        <span
            role="button"
            tabIndex={0}
            aria-label={`تغییر ترتیب ${fundTitle(item.name)}`}
            onPointerDown={(e) => controls.start(e)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
                if (e.key === 'ArrowUp') {
                    e.preventDefault()
                    onMove(-1)
                }
                if (e.key === 'ArrowDown') {
                    e.preventDefault()
                    onMove(1)
                }
            }}
            // `touch-none` is load-bearing: without it the browser claims the
            // gesture and scrolls the page instead of dragging the card.
            className="shrink-0 touch-none cursor-grab active:cursor-grabbing -mr-1 py-1 pl-1 text-text-muted"
        >
            <MdDragIndicator size={20} />
        </span>
    ) : null

    return (
        <Reorder.Item
            as="div"
            value={item.id}
            dragListener={false}
            dragControls={controls}
            // Drag lifecycle, not `onReorder`: these fire once per gesture even
            // though the list reorders several times inside one.
            onDragStart={() => {
                justDragged.current = true
                onDragStart()
            }}
            onDragEnd={() => {
                onDragEnd()
                window.setTimeout(() => {
                    justDragged.current = false
                }, 0)
            }}
            onClickCapture={(e) => {
                if (!justDragged.current) return
                e.preventDefault()
                e.stopPropagation()
            }}
            whileDrag={{
                scale: 1.02,
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)',
            }}
            // Matches the card's own radius so `whileDrag`'s shadow follows the
            // corners instead of boxing them.
            className="rounded-card"
        >
            <Item
                item={item}
                dragHandle={handle}
                fromSearch={fromSearch}
                rowIndex={rowIndex}
            />
        </Reorder.Item>
    )
}
